import * as React from 'react'

import { api, PasswordStrength } from '@owl-nest/api-client/latest'
import * as service from '@owl-nest/services'
import { t } from '@owl-nest/localize'

import { validateNewPassword } from './validators'

export type PasswordFeedback = { score: PasswordStrength; strength: string; suggestions: string[] }

type PasswordValidator = (
  password: string,
  userInputs?: string[] | undefined,
) => { feedback: { warning: string; suggestions: string[] } }

let zxcvbn: PasswordValidator | undefined = undefined
async function validate(
  password: string,
  userInputs?: string[] | undefined,
): Promise<{ feedback: { warning: string; suggestions: string[] } }> {
  if (zxcvbn === undefined) {
    zxcvbn = (await import('zxcvbn')).default
  }
  return zxcvbn(password, userInputs)
}

export function useNewPasswordValidator(): [
  PasswordFeedback | undefined,
  (password: string) => Promise<string | undefined>,
] {
  const [feedback, setFeedback] = React.useState<PasswordFeedback | undefined>(undefined)
  const dispatch = service.message.useDispatch()

  const strengths = {
    worst: `🚫 ${t('Worst')}`,
    bad: `👎 ${t('Bad')}`,
    weak: `😐 ${t('Weak')}`,
    good: `😊 ${t('Good')}`,
    strong: `🤗 ${t('Strong')}`,
  }
  const messages = {
    worst: t('Risky.'),
    bad: t('Still too weak.'),
    weak: t('Ok but still a bit too weak.'),
    good: t('Your password is secure.'),
    strong: t('Your password is very secure, you are unhackable.'),
  } as const

  async function queryFeedback(password: string): Promise<PasswordFeedback | undefined> {
    const { feedback: zxcvbnFeedback } = await validate(password)

    const suggestions = [zxcvbnFeedback.warning, ...zxcvbnFeedback.suggestions].map((key) => {
      return key && t(key) // t('') will throw
    })

    if (password === '') {
      const feedback: PasswordFeedback = {
        score: 'worst',
        strength: strengths['worst'],
        suggestions: [messages['worst'], ...suggestions],
      }
      setFeedback(feedback)
      return feedback
    }

    const result = await api.post.passwordScore({
      body: { password },
    })

    return result.caseOf({
      left: (_error) => {
        /* TODO: log sentry ? */
        dispatch(service.message.internalError())
        return feedback
      },
      right: (success) => {
        const feedback: PasswordFeedback = {
          score: success.body.score,
          strength: strengths[success.body.score],
          suggestions: [messages[success.body.score], ...suggestions],
        }
        setFeedback(feedback)
        return feedback
      },
    })
  }

  return [feedback, validateNewPassword(queryFeedback)]
}
